import React, { useContext, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import CloseIcon from '@mui/icons-material/Close'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import AnDateField from '../../../../../components/DateTime/DateField'
import API from '../../../../../services/API'
import { VehicleContext } from '../../SingleVehicle'
import { GlobalContext, TenantSettingsContext } from '../../../../../App'
import { getCurrencySymbol } from '../../../../../helpers/currency'
import DocumentsField from '../../../../../helpers/DocumentsField'
import { uploadDocument } from '../../../../../helpers/uploadDocument'

// The form draws its own header/footer and scrolls only its body, so the
// BigModal shell around it contributes no padding or scrolling - just a fixed
// comfortable width. (Scrolling the shell under sticky bars instead leaves a
// hairline of content bleeding past the footer: the shell is transform-centred
// at a fractional height.)
export const serviceModalStyle = {
  padding: 0,
  overflow: 'hidden',
  width: { xs: 'calc(100vw - 24px)', sm: 'min(720px, 92vw)' },
  borderRadius: 2,
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Flat card with an icon-led header - the same visual language as the
// Add Shift popup and the SectionKit panels on the vehicle page.
export const Section = ({ icon, title, caption, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1.25} mb={1.75}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          bgcolor: 'anMuted.main',
          color: 'contrast.main',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
    </Stack>
    {children}
  </Paper>
)

// `prefill` seeds a new service from a planned downtime window, and
// `plannedUnavailabilityId` makes the save mark that window completed.
const AddNewService = ({
  onClose,
  existingService,
  prefill,
  plannedUnavailabilityId,
}) => {
  const [date, setDate] = useState(existingService?.date ?? prefill?.date ?? null)
  const [mileage, setMileage] = useState(existingService?.mileage ?? '')
  const [garage, setGarage] = useState(existingService?.garage ?? '')
  const [workCompleted, setWorkCompleted] = useState(
    existingService?.workCompleted ?? prefill?.workCompleted ?? '',
  )
  const [cost, setCost] = useState(
    existingService?.cost != null
      ? Number(existingService.cost).toFixed(2)
      : '',
  )
  const [removedDocuments, setRemovedDocuments] = useState([])
  const [files, setFiles] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const { loadVehicle, vehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const isEdit = !!existingService
  const existingDocuments = existingService?.documents ?? []

  const toggleRemoveDocument = (key) =>
    setRemovedDocuments((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    )

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      // One at a time - presigned URLs only live for 60s, so PUTs queued
      // behind the browser's connection limit can expire before they start
      const uploadedKeys = []
      for (const file of files) {
        uploadedKeys.push(await uploadDocument(file, 'vehicles/documents'))
      }

      const res = await API.put('/fleet/' + vehicle._id, {
        service: {
          date,
          mileage,
          garage,
          workCompleted,
          documents: [
            ...existingDocuments.filter((x) => !removedDocuments.includes(x)),
            ...uploadedKeys,
          ],
          cost: cost === '' ? undefined : parseFloat(cost),
        },
        ...(existingService?._id ? { serviceId: existingService._id } : {}),
        ...(!existingService?._id && plannedUnavailabilityId
          ? { plannedUnavailabilityId }
          : {}),
      })
      if (res.status !== 200) throw new Error('Save failed')

      // Only once the record no longer references them
      await Promise.allSettled(
        removedDocuments.map((key) => API.post('/deletes3object', { key })),
      )

      setToast({ message: isEdit ? 'Service updated' : 'Service added' })
      loadVehicle()
      onClose()
    } catch (err) {
      console.error('Unable to save service', err)
      setError(
        files.length
          ? 'Unable to save this service - a document may have failed to upload. Check your connection and try again.'
          : 'Unable to save this service. Please try again.',
      )
      setSaving(false)
    }
  }

  const pendingChanges = [
    files.length > 0 && `${plural(files.length, 'file')} to upload`,
    removedDocuments.length > 0 &&
      `${plural(removedDocuments.length, 'document')} to remove`,
  ].filter(Boolean)

  const updatedByName = [
    existingService?.updatedBy?.firstName,
    existingService?.updatedBy?.lastName,
  ]
    .filter(Boolean)
    .join(' ')

  const footerCaption =
    saving && files.length
      ? 'Uploading documents…'
      : pendingChanges.length
        ? pendingChanges.join(' · ')
        : existingService?.updatedAt
          ? `Last updated${updatedByName ? ` by ${updatedByName}` : ''} on ${moment(
              existingService.updatedAt,
            ).format('D MMM YYYY')}`
          : 'Fields marked * are required'

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      // Matches BigModal's own max height so the shell never has to scroll
      sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}
    >
      {/* ---- Header ---- */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <BuildRoundedIcon />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {isEdit ? 'Edit service' : 'Add service'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[vehicle?.callSign, vehicle?.regPlate]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          <Tooltip title="Close">
            <span>
              <IconButton
                onClick={onClose}
                size="small"
                aria-label="Close"
                disabled={saving}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* ---- Body ---- */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: 'background.default',
        }}
      >
        <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Section
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            title="Service details"
            caption="When and where the work was done, and what it cost"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <AnDateField
                  date={date}
                  onChange={(newDate) => setDate(newDate)}
                  label="Date"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={garage}
                  onChange={(e) => setGarage(e.target.value)}
                  label="Garage"
                  autoComplete="off"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value)}
                  label="Mileage"
                  autoComplete="off"
                  inputProps={{ inputMode: 'numeric' }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  label="Cost"
                  autoComplete="off"
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0, step: 0.01 },
                    startAdornment: (
                      <InputAdornment position="start">
                        {getCurrencySymbol(orgSettings?.currency)}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  value={workCompleted}
                  onChange={(e) => setWorkCompleted(e.target.value)}
                  label="Work completed"
                  placeholder="e.g. Full service, front brake pads and discs replaced"
                  autoComplete="off"
                  fullWidth
                  multiline
                  minRows={3}
                  required
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            icon={<AttachFileRoundedIcon fontSize="small" />}
            title="Documents"
            caption="Optional"
          >
            <DocumentsField
              existingDocuments={existingDocuments}
              removedDocuments={removedDocuments}
              onToggleRemove={toggleRemoveDocument}
              files={files}
              onFilesChange={setFiles}
              disabled={saving}
            />
          </Section>
        </Stack>
      </Box>

      {/* ---- Footer ---- */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 1.5,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1.5}
        >
          <Typography variant="caption" color="text.secondary">
            {footerCaption}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!moment(date).isValid() || saving}
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add service'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  )
}

export default AddNewService
